import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AOS from 'aos';
import { AdminProvider } from './contexts/AdminContext';
import { UserAuthProvider } from './contexts/UserAuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import AdminRoute from './components/admin/AdminRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthRedirect from './components/auth/AuthRedirect';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import DynamicServicePage from './pages/services/DynamicServicePage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import EmailVerification from './pages/auth/EmailVerification';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/dashboard/Dashboard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import PackageManagement from './pages/admin/PackageManagement';
import TemplateBuilder from './pages/admin/TemplateBuilder';
import TemplatePreview from './pages/admin/TemplatePreview';
import CountryManagement from './pages/admin/CountryManagement';
import RequestManagement from './pages/admin/RequestManagement';
import TicketManagement from './pages/admin/TicketManagement';
import AdminTicketDetail from './pages/admin/AdminTicketDetail';
import UserManagement from './pages/admin/UserManagement';
import EmailTemplateManagement from './pages/admin/EmailTemplateManagement';
import EmailTemplateEditor from './pages/admin/EmailTemplateEditor';
import EmailTemplateTest from './pages/admin/EmailTemplateTest';
import ServicePageManagement from './pages/admin/ServicePageManagement';
import PageBuilder from './pages/admin/PageBuilder';
import Settings from './pages/admin/Settings';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

function App() {
  const { i18n } = useTranslation();
  
  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  React.useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
      easing: 'ease-out-cubic'
    });
  }, []);

  return (
    <SettingsProvider>
      <UserAuthProvider>
        <AdminProvider>
          <Router>
          <div className="App">
          <Routes>
            {/* Public routes with navbar */}
            <Route path="/" element={
              <>
                <Navbar />
                <Home />
                <Footer />
              </>
            } />
            <Route path="/about" element={
              <>
                <Navbar />
                <AboutUs />
                <Footer />
              </>
            } />
            <Route path="/contact" element={
              <>
                <Navbar />
                <ContactUs />
                <Footer />
              </>
            } />
            <Route path="/services/:countrySlug" element={
              <>
                <Navbar />
                <DynamicServicePage />
                <Footer />
              </>
            } />
            <Route path="/login" element={
              <AuthRedirect>
                <Navbar />
                <Login />
                <Footer />
              </AuthRedirect>
            } />
            <Route path="/register" element={
              <AuthRedirect>
                <Navbar />
                <Register />
                <Footer />
              </AuthRedirect>
            } />
            <Route path="/verify-email" element={
              <>
                <Navbar />
                <EmailVerification />
                <Footer />
              </>
            } />
            <Route path="/forgot-password" element={
              <>
                <Navbar />
                <ForgotPassword />
                <Footer />
              </>
            } />
            <Route path="/reset-password" element={
              <>
                <Navbar />
                <ResetPassword />
                <Footer />
              </>
            } />
            <Route path="/privacy-policy" element={
              <>
                <Navbar />
                <PrivacyPolicy />
                <Footer />
              </>
            } />
            <Route path="/terms-of-service" element={
              <>
                <Navbar />
                <TermsOfService />
                <Footer />
              </>
            } />
            <Route path="/dashboard/*" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />

            {/* Admin routes without main navbar */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/packages" element={
              <AdminRoute requiredRole="admin">
                <PackageManagement />
              </AdminRoute>
            } />
            <Route path="/admin/templates/builder/:id" element={
              <AdminRoute requiredRole="admin">
                <TemplateBuilder />
              </AdminRoute>
            } />
            <Route path="/admin/templates/preview/:id" element={
              <AdminRoute requiredRole="admin">
                <TemplatePreview />
              </AdminRoute>
            } />
            <Route path="/admin/countries" element={
              <AdminRoute requiredRole="admin">
                <CountryManagement />
              </AdminRoute>
            } />
            <Route path="/admin/requests" element={
              <AdminRoute>
                <RequestManagement />
              </AdminRoute>
            } />
            <Route path="/admin/tickets" element={
              <AdminRoute>
                <TicketManagement />
              </AdminRoute>
            } />
            <Route path="/admin/tickets/:ticketId" element={
              <AdminRoute>
                <AdminTicketDetail />
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute requiredRole="admin">
                <UserManagement />
              </AdminRoute>
            } />
            <Route path="/admin/email-templates" element={
              <AdminRoute requiredRole="admin">
                <EmailTemplateManagement />
              </AdminRoute>
            } />
            <Route path="/admin/email-templates/edit/:id" element={
              <AdminRoute requiredRole="admin">
                <EmailTemplateEditor />
              </AdminRoute>
            } />
            <Route path="/admin/email-templates/test/:id" element={
              <AdminRoute requiredRole="admin">
                <EmailTemplateTest />
              </AdminRoute>
            } />
            <Route path="/admin/service-pages" element={
              <AdminRoute requiredRole="admin">
                <ServicePageManagement />
              </AdminRoute>
            } />
            <Route path="/admin/page-builder" element={
              <AdminRoute requiredRole="admin">
                <PageBuilder />
              </AdminRoute>
            } />
            <Route path="/admin/settings" element={
              <AdminRoute requiredRole="admin">
                <Settings />
              </AdminRoute>
            } />

            {/* 404 Not Found - Must be last route */}
            <Route path="*" element={
              <>
                <Navbar />
                <NotFound />
                <Footer />
              </>
            } />
          </Routes>
          </div>
          </Router>
        </AdminProvider>
      </UserAuthProvider>
    </SettingsProvider>
  );
}

export default App;
